import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Avatar, Grid,
  Chip, LinearProgress, Button
} from '@mui/material';
import {
  Person as PersonIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import ModernLayout from '../common/ModernLayout';
import { StatsCard } from '../common/ModernCard';
import { GradientButton } from '../common/ModernButton';
import axiosInstance from '../axiosConfig';

const WhoIsWorking = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchWorkingEmployees();
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(fetchWorkingEmployees, 15000); // 15 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const fetchWorkingEmployees = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/schedule/who-is-working');
      setEmployees(response.data.data || []);
    } catch (error) {
      console.error('Error fetching working employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (hours) => {
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const getActivityColor = (activity) => {
    const colors = {
      working: '#4CAF50',
      break: '#FF9800',
      traveling: '#2196F3',
      at_office: '#9C27B0',
      remote: '#00BCD4'
    };
    return colors[activity] || '#757575';
  };

  const groupByDepartment = () => {
    const grouped = {};
    employees.forEach(emp => {
      if (!grouped[emp.department]) {
        grouped[emp.department] = [];
      }
      grouped[emp.department].push(emp);
    });
    return grouped;
  };

  const departmentGroups = groupByDepartment();

  return (
    <ModernLayout title="¿Quién está trabajando?">
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Empleados Activos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Actualizado: {new Date().toLocaleTimeString()}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant={autoRefresh ? 'contained' : 'outlined'}
              onClick={() => setAutoRefresh(!autoRefresh)}
              size="small"
            >
              Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
            </Button>
            <GradientButton
              startIcon={<RefreshIcon />}
              onClick={fetchWorkingEmployees}
              disabled={loading}
            >
              Actualizar
            </GradientButton>
          </Box>
        </Box>

        {/* Stats */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Total Activos"
              value={employees.length.toString()}
              subtitle="Empleados trabajando"
              icon={<PersonIcon />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Departamentos"
              value={Object.keys(departmentGroups).length.toString()}
              subtitle="Áreas activas"
              icon={<PersonIcon />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Horas Totales"
              value={employees.reduce((sum, e) => sum + (e.hours_worked || 0), 0).toFixed(1)}
              subtitle="Acumuladas hoy"
              icon={<TimeIcon />}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Con Ubicación"
              value={employees.filter(e => e.latitude).length.toString()}
              subtitle="GPS activo"
              icon={<LocationIcon />}
              color="info"
            />
          </Grid>
        </Grid>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Employees by Department */}
        {Object.entries(departmentGroups).map(([department, deptEmployees]) => (
          <Card key={department} sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {department}
                </Typography>
                <Chip label={`${deptEmployees.length} empleados`} color="primary" />
              </Box>

              <Grid container spacing={2}>
                {deptEmployees.map((employee) => (
                  <Grid item xs={12} sm={6} md={4} key={employee.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: 4,
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar
                            sx={{
                              bgcolor: getActivityColor(employee.activity_type),
                              width: 48,
                              height: 48
                            }}
                          >
                            {employee.employee_name[0]}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {employee.employee_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {employee.employee_role}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Chip
                            label={employee.shift_name}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TimeIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {formatDuration(employee.hours_worked * 3600)}
                            </Typography>
                          </Box>

                          {employee.location_address && (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <LocationIcon fontSize="small" color="action" />
                              <Typography variant="caption" color="text.secondary">
                                {employee.location_address}
                              </Typography>
                            </Box>
                          )}

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Check-in: {new Date(employee.checked_in_at).toLocaleTimeString()}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        ))}

        {employees.length === 0 && !loading && (
          <Card>
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No hay empleados trabajando actualmente
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </ModernLayout>
  );
};

export default WhoIsWorking;

